// Bias helpers
import { getBiasLabel as getBiasLabelInfo } from './bias'
import { RELIABILITY, biasBgClass, reliabilityDots } from '../constants/bias.js'

// Single source of truth for the 7-tier label lives in ./bias.js.
export function getBiasLabel(bias) {
  return getBiasLabelInfo(bias).label
}

// Bias dot color: single-sourced from constants/bias.js.
export function getBiasColor(bias) {
  return biasBgClass(bias)
}

// Reliability helpers
export function getReliabilityLabel(reliability) {
  if (reliability >= RELIABILITY.reliable) return 'Reliable'
  if (reliability >= RELIABILITY.mostly) return 'Mostly Reliable'
  if (reliability >= RELIABILITY.mixed) return 'Mixed'
  return 'Unreliable'
}

// One plain sentence with an outlet's rating, for its page and its meta
// description. The page said it only as loose tags ("Right Mixed · Free"),
// so it ranked for names in its headline list rather than for searches like
// "epoch times reliability" (2026-10-05 SEO audit).
const LEAN_PHRASE = {
  'Far Left': 'is rated far left',
  Left: 'is rated left',
  'Leans Left': 'leans left',
  Center: 'is rated center',
  'Leans Right': 'leans right',
  Right: 'is rated right',
  'Far Right': 'is rated far right',
}
const RELIABILITY_PHRASE = {
  Reliable: 'is rated reliable',
  'Mostly Reliable': 'is rated mostly reliable',
  Mixed: 'has mixed reliability',
  Unreliable: 'has low reliability',
}
export function ratingSentence(outlet) {
  const lean = LEAN_PHRASE[getBiasLabel(outlet.bias)]
  const reliability =
    RELIABILITY_PHRASE[getReliabilityLabel(outlet.reliability)]
  // "is rated center and reliable", not "is rated center and is rated reliable"
  const both =
    lean.startsWith('is rated ') && reliability.startsWith('is rated ')
  const rest = both ? reliability.slice('is rated '.length) : reliability
  return `${outlet.name} ${lean} and ${rest}.`
}

export function getReliabilityDots(reliability) {
  return reliabilityDots(reliability)
}

// Access helpers
// sources.access is stored lowercase ('free' | 'limited' | 'paid', enforced by
// a check constraint); labels are display only. Normalizing anyway keeps a
// stray value from falling through to the default, which is how the New York
// Times once showed as Free (2026-09-25).
export function normalizeAccess(access) {
  const v = String(access || '').trim().toLowerCase()
  return v === 'paid' || v === 'limited' ? v : 'free'
}

// Ink only (2026-10-31): blue and red mean lean on Pano, so a paywall badge
// can't borrow them, and green and amber aren't in the palette. The three
// step from open to closed by weight: a hairline, an ink rule, solid ink.
export function getAccessConfig(access) {
  switch (normalizeAccess(access)) {
    case 'paid':
      return {
        label: 'Paid',
        className: 'bg-foreground text-background',
        tooltip: 'Requires a subscription to access content',
      }
    case 'limited':
      return {
        label: 'Limited',
        className: 'border border-foreground text-foreground',
        tooltip: 'Limited free articles per month',
      }
    default:
      return {
        label: 'Free',
        className: 'border border-border text-muted-foreground',
        tooltip: 'Free to access',
      }
  }
}
